import { useInfiniteQuery } from '@tanstack/react-query';

import { listNotifications } from '@/lib/api/generated/api';
import type { NotificationPage } from '@/lib/api/generated/model';

import { notificationKeys, type InboxFilter } from '../queryKeys';

const PAGE_SIZE = 50;

/** The bell lists the newest 30 (spec 147 FR-005); the page holds the rest. */
export const BELL_LIMIT = 30;

/**
 * The inbox, newest first, a page at a time (spec 131 US2). Paged by the
 * service's `before` cursor, because the inbox grows at the top while it is
 * being read.
 */
export function useInbox(
  filter: InboxFilter,
  enabled = true,
  limit = PAGE_SIZE,
) {
  const query = useInfiniteQuery({
    queryKey: notificationKeys.list(filter),
    queryFn: async ({ pageParam }): Promise<NotificationPage> => {
      const response = await listNotifications({
        limit,
        unread: filter.unread,
        group: filter.group,
        workspace_id: filter.workspaceId,
        archived: filter.archived,
        before: pageParam,
      });
      if (response.status !== 200) throw new Error('inbox unavailable');
      return response.data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.next_before ?? undefined,
    enabled,
    retry: false,
  });
  return {
    notifications: query.data?.pages.flatMap((page) => page.items),
    isPending: query.isPending,
    loadFailed: query.isError,
    refetch: query.refetch,
    hasMore: query.hasNextPage,
    loadMore: query.fetchNextPage,
    loadingMore: query.isFetchingNextPage,
  };
}
